import { html, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, query, queryAll, state } from 'lit/decorators.js';
import { WEB_COMPONENT_HEALTH_DASHBOARD_LICENSES } from '../../../../webcomponents.constants';
import { DisplayStyle, LicenseItemDetails, LicenseItemRenderDetails, ProductStatusTexts, StatusColors } from '../types';
import { styles } from '../view/styles';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { translateFn } from '../../../../utils/translator.helper';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { GetLicenseInfo, LicenseInfo, ProductStatus } from '@nirvana/shared/dtos/types/widgets/common';
import { NavigationConstants, NavigationHelper } from '@nirvana/shared/navigation';
import { selectDashboardFilterValues } from '../../../../genericDashboard/redux/dashboard.filters.slice';
import { store } from '../../../../redux/store';
import { GenericDashboardsFiltersHelper } from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { exitFullscreen, onExitFullscreenError } from '../../../../utils/fullscreen.utils';

@customElement(WEB_COMPONENT_HEALTH_DASHBOARD_LICENSES)
export class GzHealthDashboardLicensesWebComponent extends GzGenericWidgetContent {
  @property({ type: Object, attribute: true, reflect: true })
  payload: { licenseInfo: GetLicenseInfo };

  @state()
  private readonly licenseDetailsMap = new Map<ProductStatus, LicenseItemRenderDetails>([
    [ProductStatus.ACTIVE, { status: translateFn(ProductStatusTexts.ACTIVE), borderColor: StatusColors.ACTIVE }],
    [
      ProductStatus.EXPIRED,
      {
        status: translateFn(ProductStatusTexts.EXPIRED),
        borderColor: StatusColors.EXPIRED,
        iconClass: 'ds-icon-notification_negative',
      },
    ],
    [ProductStatus.TRIAL, { status: translateFn(ProductStatusTexts.TRIAL), borderColor: StatusColors.TRIAL }],
  ]);

  @query('#licenses-container')
  licensesContainer: HTMLElement;

  @queryAll('.license-item')
  licensesItems: HTMLElement[];

  @state() private emptyData: Array<LicenseInfo>;

  /**
   * Last displayed license item
   */
  private lastDisplayedLicenseItem: HTMLElement;
  private horizontalPadding: number;
  private verticalPadding: number;

  changedInfoBoxText = `
    <gz-tooltip isEllipsis tooltipText="${translateFn('You have <b>updates</b> for this widget!')}">
      ${translateFn('You have <b>updates</b> for this widget!')}
    </gz-tooltip>
    <br />
    <br />
    <gz-tooltip
      isEllipsis
      class="three-line-tooltip"
      tooltipText="${translateFn(
        'To experience the latest design, close the current widget and select the new ones in the Settings panel. Apply your changes and save the view.'
      )}"
    >
      ${translateFn(
        'To experience the latest design, close the current widget and select the new ones in the Settings panel. Apply your changes and save the view.'
      )}
    </gz-tooltip>`;

  static get styles() {
    return styles;
  }

  firstUpdated(): void {
    super.firstUpdated();
    if (this.parentElement?.style) {
      this.parentElement.style.overflow = 'hidden';
    }
    const { verticalPadding, horizontalPadding } = this.getPaddingConstants();
    this.verticalPadding = verticalPadding;
    this.horizontalPadding = horizontalPadding;

    this.emptyData = Array(20).fill({ name: translateFn('Product name'), status: ProductStatus.TRIAL });
  }

  updated(properties: PropertyValues): void {
    if (
      (properties.has('currentWidthInterval') && properties.get('currentWidthInterval')) ||
      (properties.has('state') && properties.get('state'))
    ) {
      setTimeout(() => this.updateDisplayedLicensesList(), 0);
    }
  }

  protected renderContent(): TemplateResult {
    const data = this.getDataToDisplay();
    return html`${this.renderNoLicenseInfoBox()} ${this.renderChangedInfoBox(this.changedInfoBoxText)}
      <div id="licenses-container">
        ${data.map(item => this.renderLicenseItem(item))} ${this.renderViewAllButton()}
      </div>`;
  }

  protected renderNoLicenseInfoBox(): TemplateResult | undefined {
    const noLicenseText = translateFn(
      'No information available. To retrieve licensing data, you require company administrator privileges.'
    );
    return super.renderNoLicenseInfoBox(noLicenseText);
  }

  protected getDataToDisplay(): Array<LicenseInfo> {
    return this.state !== WidgetStateFrontEnd.IS_ACTIVE && !this.payload?.licenseInfo.products
      ? this.emptyData
      : this.payload?.licenseInfo?.products ?? [];
  }

  protected renderLicenseItem(item: LicenseInfo): TemplateResult {
    const name =
      this.state === WidgetStateFrontEnd.IS_ERROR || this.state === WidgetStateFrontEnd.IS_INFO ? '...' : item.name;
    return html`<gz-widget-product-item
      class="license-item"
      .name=${name}
      .description="${this.getLicenseDetail(item, LicenseItemDetails.STATUS)}"
      .borderColor=${this.getLicenseDetail(item, LicenseItemDetails.BORDER_COLOR)}
      .iconClass=${this.getLicenseDetail(item, LicenseItemDetails.ICON_CLASS)}
    /> `;
  }

  protected renderLoader(): TemplateResult {
    return html`
      <div class="loading-container">
        <div class="loading-section"><gz-loader rows="3"></gz-loader></div>
      </div>
    `;
  }

  protected renderViewAllButton(): TemplateResult | undefined {
    if (this.state !== WidgetStateFrontEnd.IS_ACTIVE) {
      return;
    }

    return html`<gz-button
      id="viewAllButton"
      class="d-flex align-items-end"
      type="quiet"
      color="primary"
      buttonheight="small"
      buttonfontsize="small"
      @click="${this.dispatchViewAllEvent}"
    >
      ${translateFn('Discover more')}
    </gz-button>`;
  }

  private dispatchViewAllEvent(): void {
    if (store.getState().dashboardsApp.uiSlice.ui.fullScreenActive) {
      exitFullscreen(() => {}, onExitFullscreenError.bind(this));
    }

    if (this.payload?.licenseInfo.companySelectedIsUsersCompany) {
      NavigationHelper.drillDownToMyCompany(NavigationConstants.MY_COMPANY.TABS.LICENSING);
    } else {
      const filters = selectDashboardFilterValues(store.getState().dashboardsApp);
      const companyId = GenericDashboardsFiltersHelper.getCompanyIdFromSingleSelectCompanyFilter(filters);
      NavigationHelper.drillDownToEditCompany(companyId, NavigationConstants.MY_COMPANY.TABS.LICENSING);
    }
  }

  protected handleResize(): void {
    super.handleResize();

    if (!this.parentElement) {
      return;
    }

    this.updateDisplayedLicensesList();
  }

  protected updateDisplayedLicensesList(): void {
    if (!this.parentElement) {
      return;
    }

    this.setLicenseMargin(this.lastDisplayedLicenseItem, 8);
    this.lastDisplayedLicenseItem = this.licensesItems[this.licensesItems.length - 1];
    this.showAllLicenseItems();

    for (const item of this.licensesItems) {
      this.updateDisplayedLicenseItem(item);
    }

    this.setLicenseMargin(this.lastDisplayedLicenseItem, 16);
  }

  protected updateDisplayedLicenseItem(item: HTMLElement): void {
    if (!this.parentElement) {
      return;
    }

    if (item.offsetTop + item.offsetHeight > this.parentElement.offsetHeight - this.verticalPadding) {
      this.setLicenseItemDisplayStyle(item, DisplayStyle.NONE);
    } else {
      this.setLicenseItemDisplayStyle(item, DisplayStyle.FLEX);
      this.lastDisplayedLicenseItem = item;
      item.style.visibility = 'visible';

      if (item.offsetLeft + item.offsetWidth > this.parentElement.offsetWidth - this.horizontalPadding) {
        item.style.width = this.parentElement.offsetWidth - 2 * this.horizontalPadding + 'px';
      }
    }
  }

  private setLicenseItemDisplayStyle(item: HTMLElement, displayStyle: DisplayStyle): void {
    if (item.style.display !== displayStyle) {
      item.style.display = displayStyle;
    }
  }

  private setLicenseMargin(item: HTMLElement, margin: number): void {
    if (item) {
      item.style.marginRight = margin + 'px';
    }
  }

  private showAllLicenseItems(): void {
    for (const item of this.licensesItems) {
      this.setLicenseItemDisplayStyle(item, DisplayStyle.FLEX);
      item.style.width = 'unset';
    }
  }

  private getLicenseDetail(item: LicenseInfo, detail: LicenseItemDetails): string | undefined {
    let status;
    if (this.state !== WidgetStateFrontEnd.IS_ACTIVE) {
      status = { status: '...', borderColor: StatusColors.DEFAULT, iconClass: undefined };
      return status[detail];
    }

    status = item.status;
    return this.licenseDetailsMap.get(status)?.[detail];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    licenses: GzHealthDashboardLicensesWebComponent;
  }
}
